/**
 * Numbering Change Indicator
 *
 * Display-only presentational component for the OUTPUT-LEVEL `numberingChange`
 * field on AutoRedlineOutput. Renders a Word-Ribbon-style icon (custom indent
 * glyphs that mirror Word's ribbon buttons + Fluent list glyphs) + tooltip in
 * the numbering gutter, plus a muted echoed prior-state label ("was 2.b.")
 * derived from `currentLabel`.
 *
 * This never applies anything — it is the read-only counterpart to the imperative
 * edit-level `ListChange`. It is rendered INSTEAD of the `numberingPreview` HTML
 * gutter (the two are mutually exclusive); precedence is enforced at the render
 * sites (AutoRedlinePreview / EditPreviewList).
 */

import React from "react";
import { TextNumberListLtr24Regular, ArrowCounterclockwise24Regular } from "@fluentui/react-icons";
import { IndentIncreaseIcon, IndentDecreaseIcon } from "./IndentIcons";
import { NumberingChange, NumberingChangeKind } from "../../interfaces/autoRedlineTypes";
import { describeNumberingChange } from "./services/listChangeHelpers";
import {
  numberingChangeIndicatorStyle,
  numberingChangeCurrentLabelStyle,
} from "./styles/editPreviewStyles";

/**
 * Minimal shared shape for a gutter icon component: takes a pixel `fontSize`
 * and an optional `aria-hidden`. Both the custom indent SVGs and the remaining
 * `@fluentui/react-icons` glyphs satisfy it, so they coexist in `ICON_BY_KIND`.
 */
type NumberingIcon = React.ComponentType<{
  fontSize?: number | string;
  role?: string;
  "aria-label"?: string;
  "aria-hidden"?: React.AriaAttributes["aria-hidden"];
}>;

/**
 * kind → ribbon icon. The indent glyphs are hand-drawn `IndentIcons` that match
 * Word's actual increase/decrease-indent buttons; the list glyphs stay on the
 * `@fluentui/react-icons` `24Regular` variants. `newList` uses the numbered-list
 * glyph; the circular-arrow "reset" glyph (`ArrowCounterclockwise24Regular`) is
 * reserved for the reset sub-case handled in the component (see `isReset`).
 */
const ICON_BY_KIND: Record<NumberingChangeKind, NumberingIcon> = {
  indentIn: IndentIncreaseIcon,
  indentOut: IndentDecreaseIcon,
  addToList: TextNumberListLtr24Regular,
  newList: TextNumberListLtr24Regular,
};

interface NumberingChangeIndicatorProps {
  /** The structured numbering change to render (kind drives icon + tooltip). */
  change: NumberingChange;
}

export const NumberingChangeIndicator: React.FC<NumberingChangeIndicatorProps> = ({ change }) => {
  const hasCurrentLabel = typeof change.currentLabel === "string" && change.currentLabel.length > 0;

  // A `newList` on a paragraph that already carried numbering (it has a prior
  // `currentLabel`) is a *reset* of existing numbering — shown with the restart
  // glyph + "Resetting the list" — as opposed to starting a list where there was
  // none, which keeps the plain numbered-list glyph + "Starting a new list".
  const isReset = change.kind === "newList" && hasCurrentLabel;

  const Icon = isReset ? ArrowCounterclockwise24Regular : ICON_BY_KIND[change.kind];
  const tooltip = isReset ? "Resetting the list" : describeNumberingChange(change.kind);

  // Unknown kind (should never happen — guard rejects it): render nothing rather
  // than crash on an undefined icon component.
  if (!Icon) return null;

  // The accessible name lives on the ICON (role="img" + aria-label), not the
  // wrapper: a role="img" wrapper makes its whole subtree presentational, which
  // would hide the "was <label>" prior-state text from screen readers. With the
  // name on the icon and the wrapper left as a plain span, both the operation and
  // the prior label are announced. `title` stays on the wrapper for the mouse
  // hover tooltip.
  return (
    <span style={numberingChangeIndicatorStyle} title={tooltip}>
      <Icon fontSize={20} role="img" aria-label={tooltip} />
      {hasCurrentLabel && (
        <span style={numberingChangeCurrentLabelStyle}>was {change.currentLabel}</span>
      )}
    </span>
  );
};
